import {
  Badge,
  Button,
  Dropdown,
  Layout,
  Menu,
  MenuProps,
  Popover,
  Row,
} from "antd";
import { ReactComponent as CollabsIcon } from "assets/images/collabs-icon.svg";
import { ReactComponent as HomeIcon } from "assets/images/home-icon.svg";
import { ReactComponent as Logo } from "assets/images/logo.svg";
import { ReactComponent as Profile } from "assets/images/profile-icon.svg";
import { ReactComponent as InfluencerIcon } from "assets/images/influencer-icon.svg";
import { ReactComponent as MessagesIcon } from "assets/images/messages-icon.svg";
import { ReactComponent as SettingsIcon } from "assets/images/settings-icon.svg";
import { ReactComponent as ReportIcon } from "assets/images/report-up-icon.svg";
import BrandPlaceholder from "assets/images/brand-placeholder.png";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  MenuOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { logout } from "helpers/auth-functions";
import ChangeBusinessModalControl from "components/businesses-modal/ChangeBusinessModalControl";
import { CollabsState, getPendingCollabsCount } from "redux-context/collabs";
import { useEffect, useMemo, useState } from "react";
import { FAQ_ACCESSED_KEY } from "constants/localStorageKeys";
import { InfluencerType } from "enums/InfluencerType";
import { BusinessState } from "redux-context/business";
import { UserState } from "redux-context/user";
import { ChatState } from "redux-context/chat";
import { DomainState } from "redux-context/domain";
import ConnectMeta from "components/connect-meta/ConnectMeta";
import { BusinessCampaignCollab } from "enums/BusinessCampaingCollab";
import { motion } from "motion/react";
import { getCreatorsListUrl } from "utils/creatorsUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCrown,
  faHeadset,
  faPaperPlane,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "components/breadcrumbs/Breadcrumbs";
import { OnboardingStep } from "enums/OnboardingStep";
import { getSupportChat } from "services/SauceService";
import { SubscriptionType } from "enums/SubscriptionType";
import { ImgWithErrorHandling } from "pages/influencers/InfluencerCard";
import "./Layout.scss";
import { usePaywall } from "components/paywall/paywallContext";
import { PaywallSource } from "components/paywall/Paywall";
import FreemiumStickyBar from "components/paywall/FreemiumStickyBar";
import {
  isPayBeforeSetupBlockedRoute,
  resolvePayBeforeSetupCampaignVariant,
  useFeatureFlags,
} from "services/featureFlags";
import Sidebar from "./Sidebar";

const FREEMIUM_BAR_HEIGHT = 44;

const { Header, Content } = Layout;

const DefaultLayout: React.FC<any> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [selectedKey, setSelectedKey] = useState("");
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [faqAccessed, setFaqAccessed] = useState<boolean>(
    localStorage.getItem(FAQ_ACCESSED_KEY) === "true",
  );

  const { openPaywall } = usePaywall();
  const { payBeforeSetupCampaignGateEnabled } = useFeatureFlags();
  const { loggedUser, loading } = useSelector((state: UserState) => state.user);
  const isAdmin = loggedUser?.roles?.includes("admin");

  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const { numbers } = useSelector((state: CollabsState) => state.collabs);
  const { totalUnreadCount } = useSelector((state: ChatState) => state.chat);
  const {
    metadata: { whiteLabel, logoUrl },
  } = useSelector((state: DomainState) => state.domain);
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;
  const showOnboarding =
    numbers.totalCollabs === 0 && numbers.totalInvited === 0;
  const isPayBeforeSetupCampaignVariant =
    resolvePayBeforeSetupCampaignVariant({
      gateEnabled: payBeforeSetupCampaignGateEnabled,
      showOnboarding,
      isFreemium: freemium,
    });

  const basicRemaining =
    subscription?.spotsUsageByType?.find(
      (item) => item.type === InfluencerType.basic,
    )?.amountRemaining || 0;

  const premiumRemaining =
    subscription?.spotsUsageByType?.find(
      (item) => item.type === InfluencerType.premium,
    )?.amountRemaining || 0;

  const isChatDetail = location.pathname.includes("/messenger/");
  const isHomePage =
    location.pathname === "/" || location.pathname === "/welcome";
  const isCampaignsPage = location.pathname === "/campaigns";
  const isCollabsPage = location.pathname === "/collabs";

  const onClickLogout = () => {
    logout(true);
    navigate("/login");
  };

  const onClickSupport = async () => {
    const params = {
      context: {
        businessId: business?._id,
      },
    };
    await getSupportChat(params);
    navigate("/messenger");
  };

  const onClickFAQ = () => {
    setFaqAccessed(true);
    navigate("/faq");
  };

  const handleNavigation = (path: string) => {
    if (isPayBeforeSetupCampaignVariant && isPayBeforeSetupBlockedRoute(path)) {
      navigate("/", { replace: true });
      return;
    }

    const tourState = (window as any).tourGuideState;
    if (tourState?.isNavigationBlocked && tourState?.showVideoTour) {
      tourState.setPendingNavigation(path);
      return;
    }
    navigate(path);
  };

  const dropdownItens: MenuProps["items"] = [
    {
      key: "business",
      label: <span style={{ fontWeight: 500 }}>{business?.businessName}</span>,
    },
    {
      key: "slots",
      label: (
        <>
          <div>
            <span style={{ display: "block", height: 20 }}>
              Slots available
            </span>
            <span style={{ fontWeight: 500 }}>
              {`${basicRemaining} Basic ∘ ${premiumRemaining} Premium`}
            </span>
          </div>
        </>
      ),
    },
    {
      key: "create-collab",
      label: (
        <Button type="primary" onClick={() => navigate("/collabs/new")}>
          Create Collab
        </Button>
      ),
    },
    {
      key: "change-business",
      label: (
        <Button type="link" style={{ textAlign: "right" }}>
          <ChangeBusinessModalControl />
        </Button>
      ),
    },
    {
      key: "logout",
      label: (
        <Button
          type="link"
          onClick={onClickLogout}
          style={{ textAlign: "right" }}
        >
          Logout
        </Button>
      ),
    },
  ];

  const memuItems = [
    {
      key: "home",
      icon: <HomeIcon />,
      label: <span className="label-strong">Home</span>,
      onClick: () => handleNavigation(`/`),
    },

    ...(business.viewMode === BusinessCampaignCollab.CollabOnly ||
      business.viewMode === BusinessCampaignCollab.CollabAndCampaign
      ? [
        {
          key: "collabs",
          icon: <CollabsIcon />,
          label: <span className="label-strong">Collabs</span>,

          onClick: () => handleNavigation(`/collabs`),
        },
      ]
      : []),

    ...(business.viewMode === BusinessCampaignCollab.CampaignOnly ||
      business.viewMode === BusinessCampaignCollab.CollabAndCampaign
      ? [
        {
          key: "campaigns",
          icon: <PlayCircleOutlined style={{ fontSize: 24 }} />,
          label: <span className="label-strong">Campaigns</span>,

          onClick: () => handleNavigation(`/campaigns`),
        },
      ]
      : []),
    {
      key: "applications",
      icon: <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 24 }} />,
      label: (
        <Badge
          count={numbers.pending}
          offset={[25, 8]}
          color="#ffac39"
          style={{ color: "#1D2023", fontWeight: 700, padding: "0 8px" }}
        >
          <span className="label-strong">Applications</span>
        </Badge>
      ),
      onClick: () => handleNavigation(`/applications`),
    },
    {
      key: "creators",
      icon: <InfluencerIcon />,
      label: (
        <span
          style={{ color: isMobile ? "#1D2023" : "#1d2023e0" }}
          className="label-strong"
        >
          Creators
        </span>
      ),
      onClick: () =>
        handleNavigation(
          getCreatorsListUrl(business?.venues?.[0]?.addressDisplay ?? null),
        ),
    },
    {
      key: "report",
      icon: <ReportIcon style={{ width: 24, height: 24 }} />,
      label: <span className="label-strong">Report</span>,
      onClick: () => handleNavigation(`/report?businessId=${business?._id}`),
    },
    {
      key: "messenger",
      icon: <MessagesIcon />,
      label: (
        <Badge
          count={totalUnreadCount}
          offset={[25, 8]}
          color="#ffac39"
          style={{ color: "#1D2023", fontWeight: 700, padding: "0 8px" }}
          className="text-strongest-small"
        >
          <span className="label-strong">Messenger</span>
        </Badge>
      ),
      onClick: () => handleNavigation(`/messenger`),
    },
    {
      key: "settings",
      icon: <SettingsIcon />,
      label: <span className="label-strong">Settings</span>,
      onClick: () => handleNavigation(`/settings`),
    },
  ];

  const windowHeight = window.innerHeight;

  useEffect(() => {
    const pathArray = location.pathname.split("/");
    const path = pathArray.length > 1 && pathArray[1] ? pathArray[1] : "home";
    setSelectedKey(path);
  }, [location.pathname]);

  useEffect(() => {
    if (
      isPayBeforeSetupCampaignVariant &&
      isPayBeforeSetupBlockedRoute(location.pathname)
    ) {
      navigate("/", { replace: true });
    }
  }, [isPayBeforeSetupCampaignVariant, location.pathname, navigate]);

  const contentStyle = useMemo(() => {
    if (isMobile && isChatDetail) {
      return {
        display: "flex",
        height: windowHeight - 32,
      };
    }

    if (isMobile) {
      return { margin: "0 16px" };
    }

    const topMargin = 120 + (freemium ? FREEMIUM_BAR_HEIGHT : 0);
    return { margin: `${topMargin}px 40px 0px 64px` };
  }, [isChatDetail, windowHeight, freemium]);

  if (isMobile)
    return (
      <Layout
        style={{ minHeight: "100vh", padding: isMobile ? 0 : "40px 80px" }}
      >
        <FreemiumStickyBar height={FREEMIUM_BAR_HEIGHT} />
        <div className={`menu content-menu ${collapsed ? "collapsed" : ""}`}>
          <div className="menu-top">
            <div className="item">
              {collapsed ? (
                <MenuOutlined onClick={() => setCollapsed(false)} />
              ) : (
                <CloseOutlined
                  className="item"
                  style={{ width: 32 }}
                  onClick={() => setCollapsed(true)}
                />
              )}
            </div>
            <div className="item center">
              {whiteLabel ? (
                <img
                  src={logoUrl || BrandPlaceholder}
                  alt="Brand"
                  style={{ width: 80 }}
                />
              ) : (
                <Logo style={{ width: 80 }} />
              )}
            </div>

            <div className="item last">
              <Dropdown
                menu={{ items: dropdownItens }}
                placement="bottomLeft"
                rootClassName="menu-principal"
              >
                <Profile style={{ width: 20, height: 20 }} />
              </Dropdown>
            </div>
          </div>
          <div className="vertical-content">
            <Menu
              hidden={collapsed}
              selectedKeys={[selectedKey]}
              mode="inline"
              onClick={(evt) => {
                setSelectedKey(evt.key);
                setCollapsed(true);
              }}
              onChange={() => {
                dispatch(getPendingCollabsCount());
              }}
              items={memuItems}
              disabled={
                loading ||
                isPayBeforeSetupCampaignVariant ||
                (!loggedUser?.defaultBusinessId && !isAdmin) ||
                business?.onboardingStep === OnboardingStep.NotStarted
              }
            />
          </div>
        </div>
        <Layout style={{ marginTop: isMobile ? `${70 + (freemium ? FREEMIUM_BAR_HEIGHT : 0)}px` : 0 }}>
          <div>
            <ConnectMeta />
          </div>

          {!isHomePage && (
            <div style={{ padding: "0 16px 8px" }}>
              <Breadcrumbs />
            </div>
          )}

          <Content style={contentStyle}>{children}</Content>
        </Layout>
      </Layout>
    );

  return (
    <Layout style={{ minHeight: "100vh", padding: 0 }}>
      <FreemiumStickyBar height={FREEMIUM_BAR_HEIGHT} />
      <Header
        style={{
          ...contentStyle,
          margin: 0,
          padding: "20px 20px 20px 0",
          height: "auto",
          position: "fixed",
          top: freemium ? FREEMIUM_BAR_HEIGHT : 0,
          zIndex: 100,
          width: "calc(100% - 200px)",
          background: "#fdf9f3",
          right: 0,
        }}
      >
        <Row justify="end" style={{ alignItems: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 16,
            }}
          >
            {/* <div>
                <span
                  style={{ display: "block", height: 20 }}
                  className="text-regular-small"
                >
                  Slots available
                </span>
                <span className="text-strong">
                  {`${basicRemaining} Basic ∘ ${premiumRemaining} Premium`}
                </span>
              </div> */}
            {!isHomePage && (
              <div style={{ padding: "0 32px" }}>
                <Breadcrumbs />
              </div>
            )}
            <div>
              <ConnectMeta />
            </div>

            <Row style={{ gap: 16, alignItems: "center" }}>
              {isCollabsPage && (
                <Button
                  type="primary"
                  onClick={() => navigate("/collabs/new")}
                  className="label-strong"
                >
                  Create Collab
                </Button>
              )}

              {isCampaignsPage && (
                <Button
                  type="primary"
                  onClick={() => navigate("/campaign/new")}
                  className="label-strong"
                >
                  Create Campaign
                </Button>
              )}

              {!freemium && (
                <Button
                  onClick={onClickSupport}
                  className="label-strong"
                  icon={<FontAwesomeIcon icon={faHeadset} />}
                  type="default"
                  style={{ backgroundColor: "#fff", borderColor: "#e6e6e6" }}
                >
                  Support
                </Button>
              )}

              {loggedUser?.email?.includes("@mustard.love") && (
                <Button
                  className={`label-strong help-faq-button
                } ${!faqAccessed ? "animate-fade-pulse" : ""}`}
                  type="default"
                  icon={<FontAwesomeIcon icon={faQuestionCircle} />}
                  onClick={onClickFAQ}
                  style={{ backgroundColor: "#fff", borderColor: "#e6e6e6" }}
                >
                  Help & FAQ
                </Button>
              )}

              {freemium && (
                <Button
                  type="primary"
                  style={{
                    transitionProperty:
                      "color, background-color, border-color, text-decoration-color, fill, stroke, -webkit-text-decoration-color",
                    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                    transitionDuration: "150ms",
                  }}
                  className="transition-all"
                  onClick={() =>
                    openPaywall({ source: PaywallSource.UpgradePlan })
                  }
                >
                  <FontAwesomeIcon icon={faCrown} />
                  Upgrade to unlock full access
                </Button>
              )}

              <div
                style={{
                  height: 32,
                  width: 1,
                  backgroundColor: "#D1D5DB",
                  margin: "0 8px",
                }}
              />

              <Popover
                content={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      textAlign: "right",
                    }}
                  >
                    <Button type="link" style={{ textAlign: "right" }}>
                      <ChangeBusinessModalControl />
                    </Button>
                    <Button
                      type="link"
                      onClick={onClickLogout}
                      style={{ textAlign: "right" }}
                    >
                      Logout
                    </Button>
                  </div>
                }
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    height: 38,
                  }}
                  className="label-strong"
                >
                  {business?.logoUrl ? (
                    <ImgWithErrorHandling
                      src={business?.logoUrl}
                      style={{ width: 32, height: 32, borderRadius: "50%" }}
                    />
                  ) : (
                    <Profile style={{ width: 32, height: 32 }} />
                  )}
                  {business?.businessName}{" "}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{ width: 12, height: 12, color: "#999" }}
                  />
                </span>
              </Popover>
            </Row>
          </div>
        </Row>
      </Header>
      <Sidebar
        selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
        memuItems={memuItems}
        collapsed={isMobile}
        setCollapsed={setCollapsed}
        forceDisabled={isPayBeforeSetupCampaignVariant}
        style={{ top: freemium ? FREEMIUM_BAR_HEIGHT : 0 }}
      />
      <Layout>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
          <Content style={{ ...contentStyle, paddingLeft: 200 }}>
            {children}
          </Content>
        </motion.div>
      </Layout>
    </Layout>
  );
};

export default DefaultLayout;
